import { ONE_LINK_TEMPLATE_URL, STORAGE_KEYS } from '../core/constants.js';
import { emitClickUrl } from '../core/events.js';
import { writeSession } from '../core/storage.js';

function sanitizeValue(value, fallback = '') {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (!normalized) return fallback;
  const sanitized = normalized
    .replace(/[^a-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return sanitized || fallback;
}

function firstValue(searchParams, keys) {
  for (const key of keys) {
    if (!searchParams.has(key)) continue;
    const value = (searchParams.get(key) || '').trim();
    if (value) return value;
  }
  return '';
}

function getReferrerHost() {
  if (!document.referrer) return '';
  try {
    return new URL(document.referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch (_error) {
    return '';
  }
}

function inferSourceAndMedium(host) {
  if (!host) return { source: 'direct', medium: 'none' };
  if (/(^|\.)google\./.test(host)) return { source: 'google', medium: 'organic' };
  if (/(^|\.)bing\./.test(host)) return { source: 'bing', medium: 'organic' };
  if (host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtu.be') {
    return { source: 'youtube', medium: 'referral' };
  }
  if (host.includes('duckduckgo.')) return { source: 'duckduckgo', medium: 'organic' };
  if (host.includes('search.brave.com')) return { source: 'brave', medium: 'organic' };
  if (host.includes('yahoo.')) return { source: 'yahoo', medium: 'organic' };
  return { source: sanitizeValue(host, 'referral'), medium: 'referral' };
}

function mapMediaSource(source, medium, hasPaidClickId) {
  const safeSource = sanitizeValue(source, 'website');
  const safeMedium = sanitizeValue(medium, '');
  const isPaid = hasPaidClickId || /^(cpc|ppc|paid|paid_social|paidsocial|display)$/.test(safeMedium);
  if (!isPaid) return safeSource;
  if (safeSource === 'google' || safeSource === 'gads') return 'googleadwords_int';
  if (safeSource === 'facebook' || safeSource === 'meta' || safeSource === 'instagram') return 'facebook';
  if (safeSource === 'reddit') return 'reddit_int';
  return safeSource;
}

function buildCampaign(campaign, source, medium) {
  const safeCampaign = sanitizeValue(campaign, '');
  if (safeCampaign) return safeCampaign;
  const safeSource = sanitizeValue(source, 'website');
  const safeMedium = sanitizeValue(medium, 'none');
  if (safeMedium === 'none' && safeSource === 'direct') return 'direct';
  return `${safeMedium}_${safeSource}`;
}

function buildRedirectUrl(searchParams) {
  const inferred = inferSourceAndMedium(getReferrerHost());
  const sourceRaw = firstValue(searchParams, ['utm_source']) || inferred.source;
  const mediumRaw = firstValue(searchParams, ['utm_medium']) || inferred.medium;
  const campaignRaw = firstValue(searchParams, ['utm_campaign']);
  const adSetRaw = firstValue(searchParams, ['utm_adset', 'adset']);
  const adRaw = firstValue(searchParams, ['utm_content']);
  const keywordRaw = firstValue(searchParams, ['keyword', 'utm_term']);

  const gclid = firstValue(searchParams, ['gclid']);
  const gbraid = firstValue(searchParams, ['gbraid']);
  const wbraid = firstValue(searchParams, ['wbraid']);
  const fbclid = firstValue(searchParams, ['fbclid']);
  const redditId = firstValue(searchParams, ['rdt_cid', 'reddit_click_id']);
  const hasPaidClickId = Boolean(gclid || gbraid || wbraid || fbclid || redditId);

  const source = mapMediaSource(sourceRaw, mediumRaw, hasPaidClickId);
  const medium = sanitizeValue(mediumRaw, inferred.medium);
  const campaign = buildCampaign(campaignRaw, sourceRaw, mediumRaw);

  const params = new URLSearchParams();
  params.set('pid', source || 'website');
  params.set('c', campaign || 'sq_base');
  params.set('af_channel', medium || 'none');

  if (adSetRaw) params.set('af_adset', adSetRaw.trim());
  if (adRaw) params.set('af_ad', adRaw.trim());
  if (keywordRaw) params.set('af_keywords', keywordRaw.trim());
  if (fbclid) params.set('af_sub2', fbclid);
  if (redditId) params.set('af_sub3', redditId);
  if (gclid || gbraid || wbraid) params.set('af_sub4', gclid || gbraid || wbraid);

  return `${ONE_LINK_TEMPLATE_URL}?${params.toString()}`;
}

function preserveExistingOneLink(searchParams) {
  const keep = [
    'pid',
    'c',
    'af_channel',
    'af_ad',
    'af_adset',
    'af_keywords',
    'gclid',
    'gbraid',
    'wbraid',
    'af_sub4',
    'fbclid',
    'af_sub2',
    'rdt_cid',
    'reddit_click_id',
    'af_sub3',
    'af_js_web',
    'af_ss_ver'
  ];

  const queryString = keep
    .filter((key) => searchParams.has(key))
    .map((key) => `${key}=${encodeURIComponent(searchParams.get(key))}`)
    .join('&');

  return `${ONE_LINK_TEMPLATE_URL}${queryString ? `?${queryString}` : ''}`;
}

function updateAnchors(clickUrl) {
  document.querySelectorAll('a[href^="https://oblikde.onelink.me/"]').forEach((anchor) => {
    anchor.href = clickUrl;
    anchor.removeAttribute('target');
  });

  document
    .querySelectorAll('a[href*="apps.apple.com"], a[href*="itunes.apple.com"]')
    .forEach((anchor) => {
      anchor.href = clickUrl;
      anchor.removeAttribute('target');
    });
}

export function initHeaderAttribution() {
  if (window.__OBLIK_HEADER_LOCK) return;
  window.__OBLIK_HEADER_LOCK = true;

  const searchParams = new URLSearchParams(window.location.search);
  const hasOneLinkParams = searchParams.has('pid') || searchParams.has('c') || searchParams.has('af_js_web');
  const clickUrl = hasOneLinkParams
    ? preserveExistingOneLink(searchParams)
    : buildRedirectUrl(searchParams);

  writeSession(STORAGE_KEYS.clickUrl, clickUrl);
  emitClickUrl(clickUrl);
  updateAnchors(clickUrl);
}
